import { useSyncExternalStore } from "react";

/**
 * Light or dark: the mode in ``joy-mode`` (light, dark or the system's), the
 * scheme each mode shows in ``joy-color-scheme-light`` / ``-dark`` - the keys
 * Joy's CssVarsProvider kept them under, kept so a person's choice outlives
 * the move - and the scheme shown as ``.dark`` on <html>, which shadcn's
 * theme follows (globals.css).
 *
 * ``setColorScheme`` names the scheme the mode in force shows - the toggle in
 * the commands sets the light mode's to dark - and what another tab chooses
 * is taken up as it is chosen.
 */

type Mode = "light" | "dark" | "system";
type ColorScheme = "light" | "dark";

const MODE_KEY = "joy-mode";
const SCHEME_KEY = "joy-color-scheme";

/** What is stored under *key*, with *fallback* written there when nothing is. */
function read<T extends string>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    if (value) return value as T;
    localStorage.setItem(key, fallback);
  } catch {
    // Storage refused (a private window): shown, if not kept.
  }
  return fallback;
}

const systemDark = window.matchMedia("(prefers-color-scheme: dark)");

let state = {
  mode: read<Mode>(MODE_KEY, "light"),
  lightColorScheme: read<ColorScheme>(`${SCHEME_KEY}-light`, "light"),
  darkColorScheme: read<ColorScheme>(`${SCHEME_KEY}-dark`, "dark"),
  systemMode: (systemDark.matches ? "dark" : "light") as ColorScheme,
};
const listeners = new Set<() => void>();

/** The mode in force: the system's, where that is the one chosen. */
function modeOf(current: typeof state) {
  return current.mode === "system" ? current.systemMode : current.mode;
}

function colorSchemeOf(current: typeof state) {
  return modeOf(current) === "dark"
    ? current.darkColorScheme
    : current.lightColorScheme;
}

function update(next: Partial<typeof state>) {
  state = { ...state, ...next };
  // shadcn's theme follows ``.dark`` on <html> (globals.css).
  document.documentElement.classList.toggle(
    "dark",
    colorSchemeOf(state) === "dark",
  );
  listeners.forEach((listener) => listener());
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage refused (a private window): shown, if not kept.
  }
}

/** The scheme the mode in force shows. */
function setColorScheme(colorScheme: ColorScheme) {
  const mode = modeOf(state);
  store(`${SCHEME_KEY}-${mode}`, colorScheme);
  update(
    mode === "dark"
      ? { darkColorScheme: colorScheme }
      : { lightColorScheme: colorScheme },
  );
}

function setMode(mode: Mode) {
  store(MODE_KEY, mode);
  update({ mode });
}

update({});
systemDark.addEventListener("change", (event) =>
  update({ systemMode: event.matches ? "dark" : "light" }),
);
window.addEventListener("storage", (event) => {
  if (event.key === MODE_KEY) {
    update({ mode: (event.newValue as Mode | null) ?? "light" });
  } else if (event.key === `${SCHEME_KEY}-light`) {
    update({
      lightColorScheme: (event.newValue as ColorScheme | null) ?? "light",
    });
  } else if (event.key === `${SCHEME_KEY}-dark`) {
    update({
      darkColorScheme: (event.newValue as ColorScheme | null) ?? "dark",
    });
  }
});

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The scheme shown and the ways to change it. */
export function useColorScheme() {
  const current = useSyncExternalStore(subscribe, () => state);
  return {
    mode: current.mode,
    colorScheme: colorSchemeOf(current),
    setMode,
    setColorScheme,
  };
}
